import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { ApplicationUser } from 'src/app/models/application-user/application-user';
import { SanctionScreeningResult } from 'src/app/models/masak/masak-sanction-screening';
import { AlertService } from './alert.service';
import { SignalRService } from './signalr.service';

/** Sunucudan gelen tamamlanmış tarama (SanctionScreeningNotification). */
export interface SanctionScreeningCompleted {
  /** CustomerSanctionScreening.Id — bildirime tıklanınca açılan tarama. */
  id: number;
  /** Manuel taramada (müşteri kartı olmadan) null; customerName o zaman taranan ad ya da TCKN. */
  customerId: number | null;
  customerName?: string | null;
  customerIdentityVerificationId?: number | null;
  result: SanctionScreeningResult;
  matchCount: number;
  bestScore?: number | null;
  /** Eşleşmelerden biri müşterinin TCKN/VKN'siyle birebir bulunduysa true. */
  identityNumberMatch: boolean;
  screenedAt: string;
  triggeredByUserId?: string | null;
}

/** MASAK Uyum ekranının menü bağlantısı (menu.ts / MenuItems.Link). */
const MASAK_MENU_LINK = 'specifications/masak-compliance';

/**
 * MASAK yaptırım taraması sonuçlarının canlı akışı (backend `MasakHub`).
 *
 * Tarama tamamlanınca (kimlik tespiti kaydı açılınca otomatik, ya da elle) sunucu sonucu
 * tenant'ın açık ekranlarına iter. Bu servis:
 *  - her ekranda bildirim gösterir: olası eşleşme uyarı olarak (tıklanınca tarama detayı
 *    açılır), temiz sonuç kısa bir bilgi olarak;
 *  - `screeningCompleted$` ile MASAK sayfasının listelerini ve KPI'larını tazeler.
 *
 * Yalnızca menüsünde MASAK Uyum ekranı olan kullanıcı bağlanır: bildirim müşteri adı taşıyor.
 * Bağlantı oturum açılınca kurulur (ApplicationUserService), çıkışta kapatılır.
 *
 * Bildirim SweetAlert2 toast'ıdır (sağ alt). SweetAlert aynı anda tek pencere gösterdiği için
 * taramayı tetikleyen akışlar ayrıca "kaydedildi" / "eşleşme bulunmadı" uyarısı göstermez —
 * gösterseydi bu bildirimin yerine geçerdi (kimlik tespiti kaydı, Yeniden Tara, müşteri kartından
 * tarama). Olası eşleşmede kayıt penceresinin uyarı penceresi bilerek duruyor.
 */
@Injectable({ providedIn: 'root' })
export class MasakHubService {
  private readonly hubUrl = '/masakHub';
  private readonly completed = new Subject<SanctionScreeningCompleted>();

  /** Tamamlanan taramalar. */
  readonly screeningCompleted$: Observable<SanctionScreeningCompleted> = this.completed.asObservable();

  /** Dinleyicinin tek sahibi burası; iki kez bağlanırsa her sonuç iki bildirim üretirdi. */
  private registered = false;

  constructor(
    private signalR: SignalRService,
    private alertService: AlertService,
    private router: Router,
  ) {}

  connect(user: ApplicationUser | null | undefined): void {
    if (this.registered || !MasakHubService.canSeeMasak(user)) {
      return;
    }

    const connection = this.signalR.start(this.hubUrl);
    if (!connection) {
      // Token yoksa start() null döner; oturum açıldığında tekrar denenir.
      return;
    }

    this.registered = true;
    connection.on('receiveSanctionScreeningCompleted', (screening: SanctionScreeningCompleted) => {
      if (!screening?.id) {
        return;
      }
      this.completed.next(screening);
      this.notify(screening);
    });
  }

  /** Çıkışta çağrılır; bayrak sıfırlanır ki sonraki oturumda yeniden bağlanılsın. */
  disconnect(): void {
    this.registered = false;
    this.signalR.stop(this.hubUrl);
  }

  private notify(screening: SanctionScreeningCompleted): void {
    const customer = escapeHtml(screening.customerName || 'Müşteri');
    const open = () => this.router.navigate(['/specifications/masak-compliance'], {
      queryParams: { screeningId: screening.id },
    });

    if (screening.result === SanctionScreeningResult.PossibleMatch) {
      const detail = screening.identityNumberMatch
        ? `<div class='fw-bold text-danger'>Kimlik numarası listedeki kayıtla aynı.</div>`
        : `<div>${screening.matchCount} olası eşleşme`
          + (screening.bestScore != null ? `, en yüksek benzerlik %${Math.round(screening.bestScore * 100)}` : '')
          + `</div>`;
      this.alertService.showClickableToastHtml(
        'warning',
        'Yaptırım listesinde olası eşleşme',
        `<div class='fw-semibold'>${customer}</div>${detail}`
          + `<div class='mt-1 fs-6 text-primary'><i class='mdi mdi-open-in-new me-1'></i>İncelemek için tıklayın</div>`,
        open,
        15000,
      );
      return;
    }

    this.alertService.showClickableToastHtml(
      'success',
      'Yaptırım taraması temiz',
      `<div>${customer}</div>`,
      open,
      4000,
    );
  }

  private static canSeeMasak(user: ApplicationUser | null | undefined): boolean {
    return (user?.menuItemList || []).some((menu) =>
      (menu.childrenMenuItem || []).some((child) => (child.link || '').replace(/^\/+/, '').startsWith(MASAK_MENU_LINK)));
  }
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
}
